import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid } from '@mui/material';
import * as React from 'react';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { CerebroInput, CerebroLabel, CerebroTextButton } from '..';
import { useApi } from '../../context';
import { QueryKeys } from '../../hooks';
import { DirectConnectMilestoneType, UpdateDirectConnectMilestoneModel } from '../../models';
import { CerebroApiEndpoints } from '../../utils/api';
import { getResponseData } from '../../utils/axios';

export interface DirectConnectMilestoneMarkRequest {
	milestoneType: DirectConnectMilestoneType;
	displayName: string;
}

interface DirectConnectMilestoneMarkCompleteDialogProps {
	dataRoomGuid: string | undefined;
	bankContactGuid: string | undefined;
	request: DirectConnectMilestoneMarkRequest | null;
	close: () => void;
}

export const DirectConnectMilestoneMarkCompleteDialog: React.FC<DirectConnectMilestoneMarkCompleteDialogProps> = ({
	dataRoomGuid,
	bankContactGuid,
	request,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const [amountFunded, setAmountFunded] = React.useState<string>('');

	React.useEffect(() => {
		if (request == null) setAmountFunded('');
	}, [request]);

	const { mutate: markComplete, isLoading } = useMutation<void, unknown, DirectConnectMilestoneMarkRequest>(
		toMark =>
			getResponseData(
				api.patch(
					CerebroApiEndpoints.Milestones.DirectConnect.UpdateLenderMilestone(dataRoomGuid, bankContactGuid),
					{
						milestoneType: toMark.milestoneType,
						amountFunded: toMark.milestoneType == 'Funded' ? Number(amountFunded) : null,
					} as UpdateDirectConnectMilestoneModel
				)
			),
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.DataRoom.LenderStatusBar(dataRoomGuid));
				snackbar.enqueueSnackbar('Milestone updated.', { variant: 'success' });
				close();
			},
			onError: () => {
				snackbar.enqueueSnackbar('There was a problem updating the milestone.', { variant: 'error' });
			},
		}
	);

	const onConfirm = () => {
		if (request != null) markComplete(request);
	};

	const isFundedInvalid = request?.milestoneType == 'Funded' && (amountFunded == '' || Number(amountFunded) <= 0);

	return (
		<Dialog open={request !== null} onClose={close}>
			<DialogTitle>Confirm {request?.displayName}</DialogTitle>
			<DialogContent>
				<DialogContentText>{getMilestoneDisplayMessage(request?.milestoneType ?? null)}</DialogContentText>
				{request?.milestoneType == 'Funded' && (
					<Grid container sx={{ marginTop: 2 }}>
						<Grid item xs={12}>
							<CerebroLabel required>Amount Funded</CerebroLabel>
							<CerebroInput
								type="number"
								value={amountFunded}
								onChange={event => setAmountFunded(event.target.value)}
							/>
						</Grid>
					</Grid>
				)}
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onConfirm} disabled={isLoading || isFundedInvalid}>
					Yes
				</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};

function getMilestoneDisplayMessage(milestoneType: DirectConnectMilestoneType | null): string {
	if (milestoneType == null) return '';

	switch (milestoneType) {
		case 'DocumentsReviewed':
			return 'Confirm Documents Reviewed: Click yes below to confirm that you have received and reviewed documents from the borrower.';
		case 'Funded':
			return 'Confirm Funded: Congratulations for funding this DirectConnect request! Please enter the amount funded and click yes below to confirm.';
		case 'TermSheetSubmitted':
			return 'Confirm Term Sheet Submitted: Click yes below to confirm you have provided terms to the borrower.';
		case 'Underwriting':
			return 'Confirm Underwriting: Click yes below to confirm the borrower has accepted terms and is in the underwriting process with your institution.';
		default:
			return '';
	}
}
